import { useEffect, useRef, useState } from 'react'
import { useT } from '../../i18n/useT'
import type { Cue } from '../../lib/subtitleConvert'
import { CUE_TEXT_DIR } from '../../lib/textDirection'
import { fromPlayback, toPlayback, type PlaybackSpan } from '../../lib/playbackAudio'

interface CuePlayerProps {
  cues: Cue[]
  /** Object URL of the audio being played — the user's own file, or the
   * rendition built from its parts. Either way it never touches the server. */
  audioUrl: string
  /** Empty when the two are the same file. When the audio is a rendition, the
   * cues are timed in the recording and the element in the rendition, and the
   * two drift apart by ~75 ms per part — so every seek and every highlight
   * goes through this. */
  spans: PlaybackSpan[]
  isRtl?: boolean
  /** When set, every cue gets an edit control and text changes flow up */
  onCueChange?: (index: number, text: string) => void
  /** When set, every cue gets a delete control — the engine sometimes repeats
   * a phrase, and a repeated line is faster to drop than to edit away */
  onCueDelete?: (index: number) => void
}

function formatTime(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m)
  return `${h > 0 ? h + ':' : ''}${mm}:${String(sec).padStart(2, '0')}`
}

/** Interactive transcript: the audio plays locally, the current cue is
 * highlighted, clicking a cue seeks straight to it, and (for the original
 * view) each line can be edited in place. */
export function CuePlayer({ cues, audioUrl, spans, isRtl, onCueChange, onCueDelete }: CuePlayerProps) {
  const t = useT()
  const audioRef = useRef<HTMLAudioElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [activeIdx, setActiveIdx] = useState(-1)
  const [editingIdx, setEditingIdx] = useState(-1)
  const [draft, setDraft] = useState('')

  function handleTimeUpdate() {
    const playing = audioRef.current?.currentTime
    if (playing == null) return
    const t = fromPlayback(spans, playing)
    const idx = cues.findIndex((c) => t >= c.start && t < c.end)
    setActiveIdx(idx)
  }

  useEffect(() => {
    if (activeIdx < 0) return
    const row = listRef.current?.querySelector<HTMLElement>(`[data-cue="${activeIdx}"]`)
    row?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [activeIdx])

  function handleCueClick(idx: number) {
    const audio = audioRef.current
    if (!audio) return
    audio.currentTime = toPlayback(spans, cues[idx].start) + 0.001
    void audio.play()
  }

  /** Clicking a line plays from there — unless the click ended a selection.
   * Dragging across the text to copy a few words is the whole point of the
   * line being selectable, and seeking mid-selection would fight it. */
  function handleTextClick(idx: number) {
    const selection = window.getSelection()?.toString() ?? ''
    if (selection.trim()) return
    handleCueClick(idx)
  }

  function startEdit(idx: number) {
    setEditingIdx(idx)
    setDraft(cues[idx].text)
  }

  function saveEdit() {
    const text = draft.trim()
    if (editingIdx >= 0 && text && text !== cues[editingIdx].text) {
      onCueChange?.(editingIdx, text)
    }
    setEditingIdx(-1)
  }

  function cancelEdit() {
    setEditingIdx(-1)
  }

  return (
    <div>
      <audio
        ref={audioRef}
        src={audioUrl}
        controls
        preload="metadata"
        onTimeUpdate={handleTimeUpdate}
        onSeeked={handleTimeUpdate}
        onEnded={() => setActiveIdx(-1)}
        className="w-full h-10 mb-3"
      />
      <div
        ref={listRef}
        className="max-h-72 overflow-y-auto space-y-0.5 pr-1"
        dir={isRtl ? 'rtl' : undefined}
      >
        {cues.map((cue, idx) =>
          idx === editingIdx ? (
            <div key={idx} data-cue={idx} className="px-3 py-2 rounded-lg bg-primary/5 border border-primary/20">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    saveEdit()
                  } else if (e.key === 'Escape') {
                    cancelEdit()
                  }
                }}
                autoFocus
                rows={2}
                className="w-full text-sm leading-relaxed border border-border rounded-lg p-2 bg-surface text-text resize-y focus:outline-none focus:border-border-focus"
              />
              <div className="mt-1.5 flex items-center gap-2">
                <button
                  onClick={saveEdit}
                  className="px-3 py-1 text-xs font-medium bg-primary text-on-accent rounded-md hover:bg-primary-hover transition-colors"
                >
                  {t('cue.save')}
                </button>
                <button
                  onClick={cancelEdit}
                  className="px-3 py-1 text-xs text-text-secondary hover:text-text rounded-md hover:bg-surface-hover transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <span className="text-[11px] text-text-muted ml-auto hidden sm:inline">{t('cue.editKeys')}</span>
              </div>
            </div>
          ) : (
            <div
              key={idx}
              data-cue={idx}
              className={`flex items-start gap-1 rounded-lg transition-colors ${
                idx === activeIdx ? 'bg-primary/10' : 'hover:bg-surface-hover'
              }`}
            >
              {/* The keyboard route to "play from this line". The text beside
                  it does the same on click but cannot be a control: text
                  inside a button can't be selected by dragging, and a second
                  tab stop for the same action would only get in the way. */}
              <button
                onClick={() => handleCueClick(idx)}
                title={t('cue.seek')}
                aria-label={`${t('cue.seek')} — ${formatTime(cue.start)}`}
                dir="ltr"
                className={`shrink-0 self-start px-3 py-2 text-xs font-medium tabular-nums cursor-pointer ${
                  idx === activeIdx ? 'text-primary' : 'text-text-muted hover:text-text'
                }`}
              >
                {formatTime(cue.start)}
              </button>
              <div
                onClick={() => handleTextClick(idx)}
                dir={CUE_TEXT_DIR}
                className={`flex-1 min-w-0 py-2 text-sm leading-relaxed whitespace-pre-wrap select-text cursor-pointer ${
                  idx === activeIdx ? 'text-text font-medium' : 'text-text'
                }`}
              >
                {cue.text}
              </div>
              {onCueDelete && cues.length > 1 && (
                <button
                  onClick={() => onCueDelete(idx)}
                  title={t('cue.delete')}
                  aria-label={t('cue.delete')}
                  className="shrink-0 p-1.5 mt-1 rounded-md text-text-muted hover:text-error hover:bg-error/10 transition-colors cursor-pointer"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              )}
              {onCueChange && (
                <button
                  onClick={() => startEdit(idx)}
                  title={t('cue.edit')}
                  aria-label={t('cue.edit')}
                  className="shrink-0 p-1.5 mt-1 mr-1 rounded-md text-text-muted hover:text-primary hover:bg-primary/10 transition-colors cursor-pointer"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.862 4.487z"
                    />
                  </svg>
                </button>
              )}
            </div>
          ),
        )}
      </div>
    </div>
  )
}
